import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react'
import { outdatedApp, reloadOption } from '../../app/chunks'
import type { Message, MessageContext, User } from '../../api/types'
import { Icon } from '../../components/Icon'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { usePopover } from '../../hooks/usePopover'
import { ATTACHMENT_ACCEPT, MAX_BODY_CHARS } from '../../lib/files'
import { isTouchDevice, TOUCH_SCREEN_QUERY } from '../../lib/platform'
import { hub } from '../../realtime/hub'
import { usePrivateReplies } from '../../state/privateReplies'
import { toast } from '../../state/toasts'
import { enqueueMessage } from '../../sync/outbox'
import type { TimelineMessage } from '../../sync/outboxMerge'
import { channelDisplayName, userName } from '../../lib/channels'
import { useContextPreview } from '../conversation/ContextQuote'
import { useMemberIds } from '../../hooks/useMemberIds'
import { useConversation } from '../conversation/ConversationContext'
import { lastEditableMessage } from '../conversation/messageRules'
import { replySummary } from '../conversation/ReplyQuote'
import { loadVoiceRecorder } from '../voice/loadVoiceRecorder'
import { canRecordVoice, requestMicrophone, type MicrophoneRequest } from '../voice/microphone'
import { useVoiceMessages } from '../voice/voiceMessages'
import type { VoiceRecorder } from '../voice/VoiceRecorder'
import { AttachPanel, useAttachPanel } from './AttachPanel'
import { findMentionQuery, insertMention, matchMentionCandidates, type MentionQuery } from './mentions'
import { MentionMenu } from './MentionMenu'
import { PendingAttachmentList } from './PendingAttachmentList'
import { pastedFiles } from './pastedFiles'
import { addFiles, useUploads, type PendingUpload } from './uploadsStore'
import { useDraft } from './useDraft'
import { EmojiPicker } from '../conversation/EmojiPicker'
import { PhotoPreview } from './PhotoPreview'

const TYPING_THROTTLE_MS = 3000
const NO_UPLOADS: PendingUpload[] = []

interface Props {
  replyTo: Message | null
  onReplyChange: (message: Message | null) => void
  /** The conversation as shown, messages still sending included: ↑ edits the newest of yours. */
  timeline: readonly TimelineMessage[]
}

export function Composer({ replyTo, onReplyChange, timeline }: Props) {
  const { channel, me, users, messagesById, editing, startEdit } = useConversation()
  const { body, setBody, loaded, persist } = useDraft(channel.id)
  const uploads = useUploads((s) => s.byChannel[channel.id] ?? NO_UPLOADS)
  // Photos and videos picked to send show full screen first, to caption and send (PhotoPreview); documents wait as chips.
  const [previewing, setPreviewing] = useState(false)
  const visualUploads = uploads.filter((u) => u.kind === 'image' || u.kind === 'video')
  if (previewing && visualUploads.length === 0) setPreviewing(false)
  const attachFiles = (files: File[]): boolean => {
    if (!files.length) return false
    addFiles(channel.id, files)
    const visual = files.every((f) => f.type.startsWith('image/') || f.type.startsWith('video/'))
    if (visual) setPreviewing(true)
    return visual
  }
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const lastTyping = useRef(0)
  const emoji = usePopover<HTMLSpanElement>()
  const [mention, setMention] = useState<MentionQuery | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)
  const [dragging, setDragging] = useState(false)
  const name = channelDisplayName(channel, users, me.id)
  // A private reply being written in a bot conversation: the channel message it answers.
  const privateReply = usePrivateReplies((s) => s.pending[channel.id])
  const clearPrivateReply = usePrivateReplies((s) => s.clear)
  const [canRecord] = useState(canRecordVoice)
  // A voice message being recorded, listened to or sent here takes the message box's place, once the recorder has loaded.
  const [Recorder, setRecorder] = useState<typeof VoiceRecorder | null>(null)
  // The microphone asked for by tapping the mic, from the tap until that voice message is over: one at a time.
  const [capture, setCapture] = useState<MicrophoneRequest | null>(null)
  const asking = useRef<MicrophoneRequest | null>(null)
  const [recorderFailed, setRecorderFailed] = useState(false)
  const voiceActive = useVoiceMessages((s) => s.byChannel[channel.id] !== undefined)
  const showRecorder = Recorder !== null && (voiceActive || capture !== null)
  const mounted = useRef(false)
  const recorderShown = useRef(false)
  // On a phone the + swaps the keyboard for the attach panel; a computer's paperclip opens the file picker.
  const touch = useMediaQuery(TOUCH_SCREEN_QUERY)
  const attach = useAttachPanel(textareaRef)
  const attachOpen = attach.phase === 'open'
  const closeAttach = attach.close

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // A voice message recorded here and not sent yet shows again on coming back; if the recorder can't be fetched, the
  // message box says so rather than leaving it out of reach.
  useEffect(() => {
    if (!voiceActive || Recorder || recorderFailed) return
    let cancelled = false
    loadVoiceRecorder().then(
      (module) => {
        if (!cancelled) setRecorder(() => module.VoiceRecorder)
      },
      () => {
        if (!cancelled) setRecorderFailed(true)
      },
    )
    return () => {
      cancelled = true
    }
  }, [voiceActive, Recorder, recorderFailed])

  // Back from a voice message (sent, deleted, or it couldn't start): the message box takes focus again, unless it has moved on.
  useEffect(() => {
    const shown = recorderShown.current
    recorderShown.current = showRecorder
    if (!shown || showRecorder) return
    const active = document.activeElement
    if (!active || active === document.body) textareaRef.current?.focus()
  }, [showRecorder])

  // A voice message takes the message box's place, and the panel goes with the keyboard's room.
  useEffect(() => {
    if (showRecorder) closeAttach()
  }, [showRecorder, closeAttach])

  const voiceClosed = useCallback(() => {
    asking.current = null
    setCapture(null)
  }, [])

  useEffect(() => {
    if (privateReply) textareaRef.current?.focus()
  }, [privateReply])

  // Restore the reply target saved with the draft.
  const restoredFor = useRef<string | null>(null)
  useEffect(() => {
    if (!loaded || restoredFor.current === channel.id) return
    restoredFor.current = channel.id
    if (loaded.replyToId) {
      const target = messagesById.get(loaded.replyToId)
      if (target) onReplyChange(target)
    }
  }, [loaded, channel.id, messagesById, onReplyChange])

  // Keep the saved reply id in step with the reply draft.
  useEffect(() => {
    if (restoredFor.current !== channel.id) return
    persist(body, replyTo?.id ?? null)
    if (replyTo) textareaRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the reply target changes
  }, [replyTo?.id])

  // Auto-grow the textarea.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = '36px'
    el.style.height = `${Math.min(116, Math.max(36, el.scrollHeight))}px`
  }, [body])

  const memberIds = useMemberIds(channel)
  const members = useMemo(
    () => memberIds.map((id) => users.get(id)).filter((u): u is User => Boolean(u)),
    [memberIds, users],
  )
  const candidates = useMemo(
    () => (mention ? matchMentionCandidates(members, mention.query, me.id) : []),
    [mention, members, me.id],
  )

  const uploading = uploads.some((u) => u.status === 'preparing' || u.status === 'uploading')
  const failedUploads = uploads.some((u) => u.status === 'error')
  const tooLong = body.length > MAX_BODY_CHARS
  const canSend = !uploading && !failedUploads && !tooLong && (body.trim().length > 0 || uploads.length > 0)

  const updateMention = (value: string, caret: number) => {
    const next = findMentionQuery(value, caret)
    setMention(next)
    setMentionIndex(0)
  }

  const chooseMention = (user: User) => {
    if (!mention) return
    const { value, caret } = insertMention(body, mention, user.username)
    setBody(value)
    setMention(null)
    requestAnimationFrame(() => {
      textareaRef.current?.focus()
      textareaRef.current?.setSelectionRange(caret, caret)
    })
  }

  const send = async ({ focusAfter = true }: { focusAfter?: boolean } = {}) => {
    if (!canSend) return
    const done = uploads.filter((u) => u.status === 'done' && u.attachmentId)
    await enqueueMessage({
      channelId: channel.id,
      body: body.replace(/\s+$/, ''),
      replyToId: replyTo?.id ?? null,
      context: privateReply ?? null,
      attachments: done.map((u) => ({
        id: u.attachmentId!,
        fileName: u.fileName,
        contentType: u.contentType,
        sizeBytes: u.sizeBytes,
        width: u.width,
        height: u.height,
        kind: u.kind,
        durationMs: u.durationMs,
      })),
    })
    setBody('')
    setMention(null)
    onReplyChange(null)
    if (privateReply) clearPrivateReply(channel.id)
    persist('', null)
    useUploads.getState().clear(channel.id)
    if (focusAfter) textareaRef.current?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && candidates.length) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        const delta = event.key === 'ArrowDown' ? 1 : -1
        setMentionIndex((i) => (i + delta + candidates.length) % candidates.length)
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        chooseMention(candidates[mentionIndex] ?? candidates[0])
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        setMention(null)
        return
      }
    }
    if (event.nativeEvent.isComposing) return
    // Escape cancels a reply or private reply being written (the emoji menu closes itself first).
    if (event.key === 'Escape' && !emoji.open && (replyTo || privateReply)) {
      event.preventDefault()
      if (replyTo) onReplyChange(null)
      else clearPrivateReply(channel.id)
      return
    }
    // ↑ in an empty message box edits your last message you can still edit (spec §3.7), or goes back to an edit
    // already open.
    if (event.key === 'ArrowUp' && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
      if (body !== '' || replyTo || privateReply || uploads.length > 0) return
      const open = editing?.form.current ? messagesById.get(editing.messageId) : undefined
      const target = open ?? lastEditableMessage(timeline, channel, me.id)
      if (!target) return
      event.preventDefault()
      if (target === 'sending') toast('Your last message is still sending')
      else if (target === 'failed') toast("Your last message wasn't sent")
      else startEdit(target, event.currentTarget)
      return
    }
    if (event.key === 'Enter' && !event.shiftKey && !isTouchDevice()) {
      event.preventDefault()
      void send()
    }
  }

  // Tap the mic to record (spec §4.2). The microphone is asked for straight away, while the recorder loads; another tap
  // meanwhile asks for nothing more.
  const startVoiceMessage = () => {
    if (asking.current) return
    let request: MicrophoneRequest
    try {
      request = requestMicrophone()
    } catch {
      toast("Couldn't start recording. Try again.", { tone: 'error' })
      return
    }
    asking.current = request
    loadVoiceRecorder().then(
      (module) => {
        if (!mounted.current) {
          request.cancel()
          return
        }
        setRecorder(() => module.VoiceRecorder)
        setCapture(request)
      },
      () => {
        asking.current = null
        request.cancel()
        toast("Couldn't start recording. Try again.", reloadOption(loadVoiceRecorder))
      },
    )
  }

  const voiceSending = () => {
    // A voice message sent while replying is the reply.
    onReplyChange(null)
    if (privateReply) clearPrivateReply(channel.id)
    persist(body, null)
  }

  const notifyTyping = () => {
    const now = Date.now()
    if (now - lastTyping.current < TYPING_THROTTLE_MS) return
    lastTyping.current = now
    void hub.typing(channel.id)
  }

  // Pasted screenshots and files attach as the file picker's do; text pastes as usual.
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = pastedFiles(event.clipboardData)
    if (files.length) {
      event.preventDefault()
      attachFiles(files)
    }
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    const files = [...event.dataTransfer.files]
    attachFiles(files)
  }

  return (
    <div
      className={dragging ? 'composer-area dragging' : 'composer-area'}
      data-no-gestures
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault()
          setDragging(true)
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
      }}
      onDrop={onDrop}
    >
      {privateReply ? <PrivateReplyDraft context={privateReply} onCancel={() => clearPrivateReply(channel.id)} /> : null}

      {replyTo ? (
        <div className="reply-draft">
          <Icon name="reply" />
          <div className="reply-draft-copy">
            <strong>Replying to {replyTo.authorId === me.id ? 'yourself' : userName(users, replyTo.authorId)}</strong>
            <p>{replySummary(replyTo)}</p>
          </div>
          <button type="button" className="icon-button" aria-label="Cancel reply" onClick={() => onReplyChange(null)}>
            <Icon name="x" />
          </button>
        </div>
      ) : null}

      <PendingAttachmentList uploads={uploads} />

      {mention && candidates.length ? (
        <MentionMenu users={candidates} activeIndex={mentionIndex} onChoose={chooseMention} />
      ) : null}

      {showRecorder ? (
        <Recorder
          channelId={channel.id}
          capture={capture}
          replyToId={replyTo?.id ?? null}
          context={privateReply ?? null}
          onSending={voiceSending}
          onClosed={voiceClosed}
        />
      ) : voiceActive && recorderFailed ? (
        <div className="voice-recorder is-failed" role="group" aria-label="Voice message">
          <Icon name="alert" />
          <span className="voice-status" role="alert">
            Couldn't open your voice message.
          </span>
          <button
            type="button"
            className="button small"
            onClick={() => (outdatedApp(loadVoiceRecorder) ? window.location.reload() : setRecorderFailed(false))}
          >
            {outdatedApp(loadVoiceRecorder) ? 'Reload the app' : 'Try again'}
          </button>
        </div>
      ) : null}

      <form
        hidden={showRecorder}
        className="composer"
        onSubmit={(e) => {
          e.preventDefault()
          void send()
        }}
      >
        {touch ? (
          <button
            type="button"
            className="icon-button attachment-trigger"
            aria-label={attachOpen ? 'Show keyboard' : 'Attach'}
            aria-expanded={attachOpen}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => (attachOpen ? attach.toKeyboard(true) : attach.open())}
          >
            <Icon name={attachOpen ? 'keyboard' : 'plus'} />
          </button>
        ) : (
          <>
            <button
              type="button"
              className="icon-button attachment-trigger"
              aria-label="Attach files"
              title="Attach files"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="paperclip" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept={ATTACHMENT_ACCEPT}
              onChange={(e) => {
                attachFiles([...(e.target.files ?? [])])
                e.target.value = ''
              }}
            />
          </>
        )}
        <div className="composer-input-wrap">
          <textarea
            ref={textareaRef}
            rows={1}
            value={body}
            placeholder={`Message ${name}`}
            aria-label={`Message ${name}`}
            aria-autocomplete="list"
            aria-expanded={Boolean(mention && candidates.length)}
            onChange={(e) => {
              setBody(e.target.value)
              updateMention(e.target.value, e.target.selectionStart)
              if (e.target.value) notifyTyping()
            }}
            onSelect={(e) => updateMention(e.currentTarget.value, e.currentTarget.selectionStart)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            // Tapped while the attach panel is open: the keyboard comes back in its place.
            onFocus={() => attach.toKeyboard(false)}
            onBlur={() => setTimeout(() => setMention(null), 150)}
          />
          <span ref={emoji.ref} style={{ display: 'contents' }}>
            <button
              type="button"
              className="icon-button emoji-trigger"
              aria-label="Add emoji"
              title="Add emoji"
              aria-expanded={emoji.open}
              onClick={emoji.toggle}
            >
              <Icon name="smile" />
            </button>
            {emoji.open ? (
              <div className="popover emoji-popover">
                <EmojiPicker
                  use="insert"
                  backLabel="Close emoji"
                  onBack={emoji.close}
                  onPick={(e) => {
                    const el = textareaRef.current
                    const start = el?.selectionStart ?? body.length
                    const end = el?.selectionEnd ?? body.length
                    const next = body.slice(0, start) + e + body.slice(end)
                    setBody(next)
                    emoji.close()
                    requestAnimationFrame(() => {
                      el?.focus()
                      el?.setSelectionRange(start + e.length, start + e.length)
                    })
                  }}
                />
              </div>
            ) : null}
          </span>
        </div>
        {/* One round button, as WhatsApp's and Messages' are: the mic while there's nothing to send, Send once there is. Each
            pops in as it takes the other's place. Pressing Send leaves focus in the message box, as the mention menu does:
            taking it would close a phone's keyboard. */}
        {canRecord && body.trim() === '' && uploads.length === 0 && !voiceActive ? (
          <button key="mic" type="button" className="button primary send-button is-mic" aria-label="Record a voice message" title="Record a voice message" onClick={startVoiceMessage}>
            <Icon name="mic" />
          </button>
        ) : (
          <button
            key="send"
            type="submit"
            className="button primary send-button"
            aria-label="Send message"
            title="Send message"
            disabled={!canSend}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Icon name="send" />
            <span>Send</span>
          </button>
        )}
      </form>
      <AttachPanel
        panel={attach}
        onFiles={(files) => {
          // Photos open full screen to caption there; a document is ready for a caption here, the keyboard back in the
          // panel's place.
          attach.toKeyboard(!attachFiles(files))
        }}
      />
      {previewing && visualUploads.length ? (
        <PhotoPreview
          uploads={visualUploads}
          caption={body}
          onCaption={setBody}
          canSend={canSend}
          onSend={() => {
            setPreviewing(false)
            void send({ focusAfter: false })
          }}
          onCancel={() => {
            setPreviewing(false)
            for (const upload of visualUploads) useUploads.getState().remove(channel.id, upload.localId)
          }}
        />
      ) : null}
      <div className="composer-note" hidden={showRecorder}>
        {tooLong ? (
          <span className="over-limit">
            {body.length}/{MAX_BODY_CHARS} characters
          </span>
        ) : null}
        <kbd>Enter</kbd> to send <span aria-hidden="true">·</span> <kbd>Shift + Enter</kbd> for a new line
      </div>
    </div>
  )
}

/** Above the composer while writing a private reply: the channel message it answers. */
function PrivateReplyDraft({ context, onCancel }: { context: MessageContext; onCancel: () => void }) {
  const { title, text } = useContextPreview(context)
  return (
    <div className="reply-draft private-reply-draft" role="note" aria-label={`${title}: ${text}`}>
      <Icon name="megaphone" />
      <div className="reply-draft-copy">
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
      <button type="button" className="icon-button" aria-label="Cancel private reply" onClick={onCancel}>
        <Icon name="x" />
      </button>
    </div>
  )
}
